'use client';
import React, { useEffect, useRef } from 'react';
import Image from 'next/image';
import { useDisclosure } from '@heroui/modal';
import useInvoiceUploadStore from '@/lib/invoice-upload/store/use-invoice-upload-store';
import ModalTemplate from '@/app/components/modal-template';
import { useTranslations } from 'next-intl';
import { Button, ModalBody, ModalContent } from '@heroui/react';
import InvoiceUpload, {
  InvoiceUploadRef,
} from '@/app/components/invoice-upload';

export default function InvoiceUploadErrorDialog() {
  const t = useTranslations('invoiceUpload.error');
  const invoiceUploadError = useInvoiceUploadStore((state) => state.error);
  const { onOpen, isOpen, onOpenChange, onClose } = useDisclosure();
  const invoiceUploadRef = useRef<InvoiceUploadRef>(null);
  const errors = [
    'zip',
    'consumption',
    'city',
    'streetname',
    'dates',
    'energiekosten',
    'pdf',
    'size',
    'cost',
    'energytype',
  ];
  const matchedError = errors.find((error) =>
    invoiceUploadError?.toLowerCase().includes(error)
  );
  const errorBodyText = matchedError
    ? t(`invalid${matchedError}`)
    : t('default');

  useEffect(() => {
    if (!isOpen && invoiceUploadError) {
      onOpen();
    }
    if (isOpen && !invoiceUploadError) {
      onClose();
    }
  }, [invoiceUploadError]);

  return (
    <ModalTemplate isOpen={isOpen} onOpenChange={onOpenChange}>
      <ModalContent>
        <ModalBody className="pt-5 md:pt-[112px] px-4 pb-5 md:pb-[70px]">
          <div className="self-center">
            <Image
              data-testid="game-over-icon"
              src="/images/game-over-icon.svg"
              alt="game over icon"
              width={160}
              height={160}
              className="self-center"
            />
          </div>
          <div>
            <p
              data-testid="invoice-upload-error-title"
              className="text-2xl font-semibold pt-9 pb-3 md:pb-4"
            >
              {t('title')}
            </p>
            <p
              data-testid="invoice-upload-error-body"
              className="text-base md:text-lg pb-6 md:pb-8"
            >
              {errorBodyText}
            </p>
            <div className="flex flex-col md:flex-row justify-between gap-3">
              <Button
                className="btn-primary btn-padded"
                size="lg"
                onPress={() => invoiceUploadRef.current?.open()}
              >
                <InvoiceUpload ref={invoiceUploadRef}>
                  {t('tryAgain')}
                </InvoiceUpload>
              </Button>
              <Button
                data-testid="manual-entry-button"
                onPress={onClose}
                className="btn-secondary btn-padded"
                size="lg"
              >
                <span data-testid="manual-entry-button-text">
                  {t('tryManually')}
                </span>
              </Button>
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </ModalTemplate>
  );
}
